/**
 * Utility functions for converting backend choice types to option arrays
 * for use in select components and filters.
 */

import { formatConstantCaseToSentenceCase } from '../string/string';

type Option<T> = {
  label: string;
  value: T;
};

/**
 * Converts a numeric enum and its labels to an array of options.
 * Filters out string keys (enum names) and only includes numeric values.
 *
 * @example
 * ```ts
 * const options = enumToOptions(OrderStatus, OrderStatusLabels);
 * // [{ label: "Active", value: 0 }, { label: "Cancelled", value: 1 }, ...]
 * ```
 */
export function enumToOptions<T extends number>(
  enumObject: Record<string, T | string>,
  labels: Record<number, string>
): Option<T>[] {
  return Object.values(enumObject)
    .filter((value): value is T => typeof value === 'number')
    .map((value) => {
      const label = labels[value];
      return {
        label: label ?? String(value),
        value
      };
    });
}

/**
 * Converts a const object (string union type) and its labels to an array of options.
 * Use when labels are keyed by the same keys as the const object (e.g. DRAFT, PENDING).
 *
 * @example
 * ```ts
 * const options = constObjectToOptions(OrderPaymentStatus, OrderPaymentStatusLabels);
 * // [{ label: "Disputed", value: "disputed" }, ...]
 * ```
 */
export function constObjectToOptions<T extends string>(
  constObject: Record<string, T>,
  labels: Record<T, string>
): Option<T>[] {
  return Object.values(constObject).map((value) => ({
    label: labels[value] ?? value,
    value
  }));
}

/**
 * Builds options from const object + labels when both share the same keys (e.g. DRAFT, PENDING).
 * Use instead of constObjectToOptions when the labels object is keyed by enum keys, not values.
 */
export function entriesToOptions<K extends string, V extends string>(
  constObject: Record<K, V>,
  labels: Record<K, string>
): Option<V>[] {
  return (Object.keys(labels) as K[]).map((key) => ({
    value: constObject[key],
    label: labels[key]
  }));
}

/**
 * Builds a value -> label map for use with getLabel when labels are keyed by enum keys.
 */
export function getLabelMapFromEntries<K extends string, V extends string>(
  constObject: Record<K, V>,
  labels: Record<K, string>
): Record<V, string> {
  return Object.fromEntries((Object.keys(labels) as K[]).map((key) => [constObject[key], labels[key]])) as Record<
    V,
    string
  >;
}

/**
 * Converts a const object to options using formatConstantCaseToSentenceCase for labels.
 * Use when no explicit label mapping exists and the enum values are CONSTANT_CASE strings.
 *
 * @example
 * ```ts
 * const options = constObjectToSentenceCaseOptions(QuoteStatus);
 * // [{ label: "Accepted", value: "ACCEPTED" }, { label: "Pending", value: "PENDING" }, ...]
 * ```
 */
export function constObjectToSentenceCaseOptions<T extends string>(constObject: Record<string, T>): Option<T>[] {
  return Object.values(constObject).map((value) => ({
    label: formatConstantCaseToSentenceCase(value),
    value
  }));
}

/**
 * Gets the label for a choice value, with a fallback that formats the value if no label exists.
 * This is safer than using `?? value` as it provides a more user-friendly fallback.
 *
 * @example
 * ```ts
 * // For string const object
 * const label = getLabel(order.orderType, OrderTypeLabels);
 * // Returns the label, or a formatted version of the value if label doesn't exist
 *
 * // For numeric enum
 * const label = getLabel(order.status, OrderStatusLabels);
 * ```
 */
export function getLabel<T extends string | number>(value: T, labels: Record<T, string>): string {
  const label = labels[value];
  if (label) {
    return label;
  }
  // Fallback: format the value to be more user-friendly
  if (typeof value === 'string') {
    // Convert snake_case or kebab-case to Title Case
    return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }
  // For numbers, return as string
  return String(value);
}
